import { string, z } from 'zod';

/**
 * A Zod schema that defines the structure of a sentiment analysis DTO (Data Transfer Object).
 * This DTO is used to represent the results of a sentiment analysis on a conversation.
 *
 * The schema includes the following properties:
 *
 * - `overallSentiment`: The general emotional tone of the conversation, classified as 'Positive', 'Neutral', or 'Negative'.
 * - `detailedSentiment`: A deeper breakdown of the sentiment, providing more context or specific emotional cues observed throughout the conversation.
 * - `contactSentiment`: A concise description of the contact’s (e.g., customer) emotional state during the conversation.
 * - `observationsAboutRepCommunication`: A list of key observations regarding the representative's communication style or approach, focusing on notable aspects like clarity, empathy, or professionalism.
 * - `suggestionsForRep`: A list of actionable feedback for the representative, aimed at improving future communication based on the current conversation’s analysis.
 * - `repSentiment`: A brief summary of the representative’s emotional tone or attitude during the conversation.
 * - `sentimentScore`: A numerical representation of the overall conversation sentiment, ranging from -100 (very negative) to 100 (very positive), providing a quantitative measure of the emotional tone.
 * - `contactSentimentScore`: An optional numerical score specifically representing the contact’s sentiment, on a scale from -100 to 100, indicating the intensity of their emotional state.
 * - `repSentimentScore`: An optional numerical score that reflects the emotional tone of the representative’s communication, ranging from -100 to 100.
 * - `emotionalShift`: A description of any significant emotional changes or fluctuations that occurred during the conversation, capturing shifts in tone, mood, or intensity.
 * - `contactTone`: A summary of the tone used by the contact, describing how they conveyed their message (e.g., calm, frustrated, enthusiastic).
 * - `repTone`: A description of the representative’s tone, focusing on their approach in conveying messages (e.g., polite, assertive, empathetic).
 */
export const conversationSentimentSchema = z.object({
  overallSentiment: z
    .enum(['Positive', 'Neutral', 'Negative'])
    .describe(
      'Overall emotional tone of the conversation, categorized as Positive, Neutral, or Negative.',
    ),

  detailedSentiment: z
    .string()
    .describe(
      'A nuanced and detailed analysis of the sentiment, describing specific emotional cues or patterns observed throughout the conversation.',
    ),

  contactSentiment: z
    .string()
    .describe(
      "A brief description of the contact's emotional state during the conversation, reflecting how they felt or reacted.",
    )
    .optional(),

  observationsAboutRepCommunication: z
    .array(string())
    .describe(
      "A list of key observations  regarding the representative's communication, highlighting significant aspects such as clarity, empathy, or professionalism.",
    ),

  suggestionsForRep: z
    .array(string())
    .describe(
      'A set of constructive feedback points for the representative, offering ways to improve their communication based on the analysis of the conversation.',
    )
    .optional(),

  repSentiment: z
    .string()
    .describe(
      "A brief summary of the representative's emotional tone or attitude during the conversation, such as calm, frustrated, or empathetic.",
    )
    .optional(),

  sentimentScore: z
    .number()
    .describe(
      'A numerical score representing the overall sentiment of the conversation, ranging from -100 (very negative) to 100 (very positive).',
    ),

  contactSentimentScore: z
    .number()
    .describe(
      'An optional numerical score indicating the sentiment of the contact, measured on a scale from -100 (very negative) to 100 (very positive).',
    )
    .optional(),

  repSentimentScore: z
    .number()
    .describe(
      "An optional numerical score reflecting the representative's emotional tone, ranging from -100 (very negative) to 100 (very positive).",
    )
    .optional(),

  emotionalShift: z
    .string()
    .describe(
      'A description of any significant emotional changes or shifts during the conversation, capturing moments where the tone or mood altered noticeably.',
    ),

  contactTone: z
    .string()
    .describe(
      'A summary of the tone used by the contact in their messages, such as whether they were polite, frustrated, enthusiastic, or assertive.',
    ),

  repTone: z
    .string()
    .describe(
      "A description of the overall tone in the representative's communication, focusing on how they expressed themselves (e.g., empathetic, assertive, formal).",
    ),

  followUpActions: z
    .array(z.string())
    .optional()
    .nullable()
    .describe(
      'Actions that need to be followed up on after the conversation, such as sending additional information or scheduling a meeting.',
    ),
});

export type ConversationSentiment = z.infer<typeof conversationSentimentSchema>;
